import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from '@klickdata/core/application/src/logger/logger.service';
import { ListingContext } from '@klickdata/core/application/src/model/model-interface';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config/src/config.service';
import { HttpErrorService } from '@klickdata/core/http/src/error/http-error.service';
import { RequestBuilderService } from '@klickdata/core/http/src/request/request-builder.service';
import { ResponseData } from '@klickdata/core/http/src/responce/responce';
import { MaterialModelType } from '@klickdata/core/resource/src/resource.model';
import { AppScope } from '@klickdata/core/resource/src/type.scope.enum';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, first, map, share, switchMap } from 'rxjs/operators';
import { Media, MediaData } from './media.model';

@Injectable()
export class MediaService {
    public mediaURL: string;
    public mediaAIURL: string;
    protected defaultMediaUrl: string;
    protected defaultNameUrl: string;
    // protected user_id: Observable<number>;
    protected customer_id: Observable<number>;
    constructor(
        protected auth: AuthService,
        protected logger: LoggerService,
        protected builder: RequestBuilderService,
        protected config: ConfigService,
        protected error: HttpErrorService,
        protected http: HttpClient
    ) {
        this.mediaURL = `${config.config.apiUrl}media`;
        this.mediaAIURL = `${config.config.apiUrl}media/ai`;
        this.defaultMediaUrl = `${this.config.config.apiUrl}default-media`;
        this.defaultNameUrl = `${this.config.config.apiUrl}default-name`;
        this.customer_id = this.auth.getCustomer().pipe(
            first(),
            map((customer) => customer.id)
        );
    }

    /**
     * Upload media!
     */
    public uploadMedia(item: File): Observable<Media> {
        const formData: FormData = new FormData();
        formData.append('file', item);

        return this.http
            .post<ResponseData<MediaData>>(this.mediaURL, formData, {
                reportProgress: true,
            })
            .pipe(map((res) => this.mapMedia(res.data)));
    }

    public createAIMedia(
        data: MediaData,
        prompt: string,
        type: MaterialModelType,
        eager?: string | string[]
    ): Observable<Media> {
        return this.builder
            .post<ResponseData<any>>(this.mediaAIURL, { ...data, prompt, type })
            .putEager(eager)
            .request()
            .pipe(map((res) => this.mapMedia(res.data as MediaData)));
    }

    /**
     * Upload media!
     */
    public uploadMediaByLink(data: MediaData, eager?: string | string[]): Observable<Media> {
        const params = {
            reportProgress: true,
        };
        if (eager) {
            params['eager'] = eager;
        }
        return this.http
            .post<ResponseData<MediaData>>(this.mediaURL, data, params)
            .pipe(map((res) => this.mapMedia(res.data)));
    }

    /**
     * Get resource media
     * @param mediaId media id of this resource.
     * @param scope Resource Scope
     * @param artnr article code for this resource
     * @param artpos article video position then we can display relative media.
     * @returns Resource Media obj.
     */
    public getMedia(
        mediaId: number,
        thumbnail_size: 'small' | 'medium' | 'large' = 'large',
        eager?: string
    ): Observable<Media> {
        return this.auth.check().pipe(
            first(),
            switchMap((authorized) => {
                if (mediaId) {
                    const url = authorized ? this.mediaURL : `${this.mediaURL}/public`;
                    return this.builder
                        .get<MediaData>(`${url}/${mediaId}`)
                        .param('size', thumbnail_size)
                        .putEager(eager)
                        .request()
                        .pipe(
                            map((res) => this.mapMedia(res.data)),
                            catchError(() => of(this.mapMedia({}))),
                            share()
                        );
                }

                return of(null);
            })
        );
    }

    /**
     * Get default media per scope.
     * @param scope media scope
     * @returns return media including object including url, default_url and fallback_url
     */
    public getMediaByPath(path: string, size?: 'small' | 'medium' | 'large'): Observable<Media> {
        const req = this.builder.get<MediaData[]>(this.mediaURL).param('path', path);
        if (size) {
            req.param('size', size);
        }
        return req.request().pipe(map((res) => (res.data?.length > 0 ? this.mapMedia(res.data[0]) : null)));
    }
    public getMediaByResourceItemId(itemId: number, size?: 'small' | 'medium' | 'large'): Observable<Media> {
        const req = this.builder.get<MediaData>(`${this.mediaURL}/guest/byResourceItem/${itemId}`);
        if (size) {
            req.param('size', size);
        }
        return req.request().pipe(map((res) => this.mapMedia(res.data)));
    }
    public listenToMediaEvent(params: { event?: string; type?: MaterialModelType }): Observable<Media> {
        if (!params.event) {
            params.event = 'MediaEvent';
        }
        return this.getMediaSocketResponse(params.event, params.type);
    }
    public listenToUpdatedMedia(params: { event?: string; type?: MaterialModelType }): Observable<Media> {
        if (!params.event) {
            params.event = 'MediaUpdatedEvent';
        }
        return this.getMediaSocketResponse(params.event, params.type);
    }
    private getMediaSocketResponse(event: string, type?: MaterialModelType): Observable<Media> {
        return this.auth.getUser().pipe(
            first(),
            switchMap((user) =>
                this.auth.listenUserChannel(event).pipe(
                    map((res: ResponseData<MediaData>) => {
                        const media = this.mapMedia(res.data);
                        media.listingContext =
                            user.id == media.author_id
                                ? ListingContext.ME
                                : user.customer_id == media.customer_id
                                ? ListingContext.ACADAMY
                                : ListingContext.KOL;
                        if (type && media.type.indexOf(type) !== -1) {
                            return media;
                        }
                        return null;
                    })
                )
            )
        );
    }

    /**
     * Get default media per scope.
     * @param scope media scope
     * @returns return media including object including url, default_url and fallback_url
     */
    public getDefaultMedia(scope: AppScope): Observable<Media> {
        return this.builder
            .get<MediaData>(`${this.defaultMediaUrl}/scope/${scope}`)
            .param('customer', this.customer_id)
            .request()
            .pipe(map((res) => this.mapMedia(res.data)));
    }

    public getDefaultName(scope: AppScope): Observable<{ [key: string]: string }> {
        return this.builder
            .get<{ [key: string]: string }>(`${this.defaultNameUrl}/scope/${scope}`)
            .param('customer', this.customer_id)
            .request()
            .pipe(map((res) => res.data));
    }

    /**
     * Get media by ids.
     * @param ids
     * @returns return medias
     */
    public getMediaByIds(ids: number[]): Observable<Media[]> {
        return this.builder
            .get<MediaData[]>(`${this.mediaURL}`)
            .param('ids', ids.join(','))
            .request()
            .pipe(map((res) => res.data.map((item) => this.mapMedia(item))));
    }

    /**
     * Post default media for scope.
     * @param scope media data
     * @returns return media including object including url, default_url and fallback_url
     */
    public setDefaultMedia(data: MediaData): Observable<Media> {
        return this.builder
            .post<MediaData>(this.defaultMediaUrl, data)
            .request()
            .pipe(map((res) => this.mapMedia(res.data)));
    }
    public updateDefaultMedia(id: number, data: MediaData): Observable<Media> {
        return this.builder
            .put<MediaData>(`${this.defaultMediaUrl}/${id}`, data)
            .request()
            .pipe(map((res) => this.mapMedia(res.data)));
    }
    public updateMedia(id: number, data: MediaData, eager?: string | string[]): Observable<Media> {
        const req = this.builder.put<MediaData>(`${this.mediaURL}/${id}`, data);
        if (eager) {
            req.putEager(eager);
        }
        return req.request().pipe(map((res) => this.mapMedia(res.data)));
    }

    public setDefaultName(data: { [key: string]: any }): Observable<{ [key: string]: string }> {
        return this.builder
            .post<{ [key: string]: string }>(this.defaultNameUrl, data)
            .request()
            .pipe(map((res) => res.data));
    }

    /**
     * Remove default scope media.
     * @param data
     * @returns
     */
    public removeDefaultMedia(id: string): Observable<any> {
        return this.builder.delete<any>(`${this.defaultMediaUrl}/${id}`).request();
    }

    public restoreDefaultMedia(id: string): Observable<Media> {
        return this.builder
            .put<MediaData>(`${this.defaultMediaUrl}/restore/${id}`, null)
            .request()
            .pipe(map((res) => this.mapMedia(res.data)));
    }
    public destroy(id: number): Observable<{ success: boolean } | {}> {
        return this.builder
            .delete<{ success: boolean }>(`${this.mediaURL}/${id}`)
            .request()
            .pipe(
                map((res) => res.data),
                catchError((res: HttpErrorResponse) => {
                    this.logger.error(res.error.error.messages[0]);
                    return EMPTY;
                })
            );
    }

    private mapMedia(data: MediaData): Media {
        const media = new Media(data);
        media.isAgentMedia = !!data.prompt_states?.length;
        return media;
    }
}
